import * as React from "react";
import { useCallback, useEffect } from "react";
import { Button, Gapped, Modal, Toast } from "react-ui";
import { useDispatch } from "react-redux";

import { hideModalActionCreator } from "../../FormModalAction";
import {
    CombinatorType,
    CombineFufResult,
    FufCombineMethod,
} from "../../../../../../../Common/CandyApiClient/Types/DraftActions/CombineFufResult";
import { CustomFile } from "../../../../../Helpers/file";
import { ModalStateInfo } from "../../IModalState";
import { isNotNullOrEmpty } from "../../../../../../../Common/TypingUtils";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { extractFileExtension, FileProps, PatchFufSteps, TableType, UploadFileStatuses } from "../Utils/Types";
import { ModalText, StepPage } from "../Utils/Styles/Modals.styles";
import {
    XML_UPLOAD_CLICK_CLOSE,
    XML_UPLOAD_FILE,
    XML_UPLOAD_FILES,
    XML_UPLOAD_SELECT_ADD,
    XML_UPLOAD_SELECT_DEEP_MERGE,
    XML_UPLOAD_SELECT_REPLACE,
} from "../../../../../Controls/metricsAction";
import { useMetrics } from "../../../../../Controls/Internals/Metrics/MetricsProviderContext";
import { UploadedFileInfo } from "../../../../ActionCreators/FufUploadActionCreator";
import { useUploadFile } from "../Utils/UploadFileRedisign/UseUploadFile";
import {
    getAcceptFilesError,
    getStepHeaderContent,
    getTableTypeHeaderContent,
    invalidFileErrorText,
} from "../Utils/TextContent";
import { CombineMethodPicker } from "../Utils/CombineMethodPicker/CombineMethodPicker";
import { useFormObject } from "../../../../../Controls/FormObjectProvider";
import { ModalLoader } from "../Utils/ModalLoader/ModalLoader";
import { FileException } from "../Utils/UploadFileRedisign/UploadFile.styles";
import { MergeResult } from "../Utils/MergeResult/MergeResult";
import { FileProperties } from "../../../../../../../Common/CandyApiClient/Types/DraftActions/FileProperties";

export interface PatchFufByAttachmentModalProps {
    modalSettings?: ModalStateInfo;
    currentGfv: string;
    reloadForm: (path?: string) => void;
    getFileProperties: (file: CustomFile | undefined) => Promise<UploadedFileInfo>;
    importXmls: (
        driveUris: string[],
        combineMethod: FufCombineMethod,
        combinatorType: CombinatorType,
        isImportByFuf: boolean,
        args?: string[]
    ) => Promise<CombineFufResult | undefined>;
}

export const NdflInquiryUpload = (props: PatchFufByAttachmentModalProps) => {
    const { modalSettings, getFileProperties, importXmls } = props;
    const dispatch = useDispatch();
    const track = useMetrics();
    const form = useFormObject();

    const [loading, setLoading] = React.useState(false);
    const [step, setStep] = React.useState<PatchFufSteps>(PatchFufSteps.SelectCombineFufMethod);
    const [modalHeader, setModalHeader] = React.useState<React.ReactNode>(<>Загрузка файлов</>);
    const [fufCombineMethod, setFufCombineMethod] = React.useState<FufCombineMethod>(FufCombineMethod.Merge);
    const [fufCombineArgs, setFufCombineArgs] = React.useState<string[]>([]);
    const [mergeResult, setMergeResult] = React.useState<CombineFufResult | undefined>(undefined);

    const memoizedFilter = useCallback(filterValidFiles, []);
    const memoizedCheckValidFile = useCallback(checkValidFile, []);

    const { filesContainer, uploadedFiles, validDriveURLs, addUserFilesToDrive } = useUploadFile<string>({
        filterValidDriveUrl: memoizedFilter,
        checkValidFile: memoizedCheckValidFile,
    });

    function filterValidFiles(fileToRemove: FileProps, url: string) {
        return url !== fileToRemove.driveUri;
    }

    React.useEffect(() => {
        setModalHeader(getStepHeaderContent(fufCombineMethod));
    }, [step]);

    useAsyncEffect(async () => {
        if (modalSettings?.currentInstancesCount === 0) {
            setStep(PatchFufSteps.SelectFiles);
            setFufCombineMethod(FufCombineMethod.Merge);
            if (modalSettings?.applyDeepMerge) {
                setFufCombineArgs(["deepMerge"]);
            }
            await addUserFilesToDrive();
        } else {
            setStep(PatchFufSteps.SelectCombineFufMethod);
        }
    }, []);

    useEffect(() => {
        setModalHeader(getTableTypeHeaderContent(modalSettings?.tableType));
    }, []);

    const onClose = async () => {
        track(XML_UPLOAD_CLICK_CLOSE, { path: modalSettings?.pathToTable });
        dispatch(hideModalActionCreator());
    };

    const onUploadFileHandler = async () => {
        const invalidFilesCount = uploadedFiles.length - validDriveURLs.length;

        track(XML_UPLOAD_FILES, {
            path: modalSettings?.pathToTable,
            filesCount: uploadedFiles.length,
            validFilesCount: validDriveURLs.length,
            invalidFilesCount: invalidFilesCount,
        });
        if (validDriveURLs.length) {
            setStep(PatchFufSteps.LoadToForm);
            const combineMethod =
                fufCombineMethod === FufCombineMethod.ReplaceThenMerge
                    ? fufCombineMethod
                    : (modalSettings?.combineType ?? FufCombineMethod.Merge);

            await uploadFiles(combineMethod);
        }
    };

    async function refreshUpload() {
        Toast.close();
        await onUploadFileHandler();
    }

    /** Валидируем файл и возвращаем новое состояние */
    async function checkValidFile(
        file: FileProps,
        setValidDriveURLs: React.Dispatch<React.SetStateAction<string[]>>
    ): Promise<FileProps> {
        let errorMessageText: string | JSX.Element = "";
        let instancesCount;
        if (!modalSettings?.acceptFiles?.includes(extractFileExtension(file.uploadFile.name))) {
            errorMessageText = getAcceptFilesError(modalSettings?.acceptFiles);
        }

        const driveUriGuard = file.driveUri;
        if (file.id && driveUriGuard && !errorMessageText) {
            const properties = await getFileProperties(file.uploadFile);
            if (properties.fileProperties?.gfv && modalSettings?.acceptGFVs?.includes(properties.fileProperties?.gfv)) {
                setValidDriveURLs(value => [...value, driveUriGuard]);
            } else {
                errorMessageText = (
                    <>
                        {invalidFileErrorText}
                        <br />
                        {modalSettings?.invalidFileErrorText ?? "Справки можно загрузить из отчетов 2-НДФЛ или 6-НДФЛ."}
                    </>
                );
            }

            const instancesCountName = modalSettings?.instanceCountFromProps?.name;
            if (instancesCountName) {
                instancesCount = properties.fileProperties?.[instancesCountName as keyof FileProperties];
            }
        }
        const status = errorMessageText ? UploadFileStatuses.error : UploadFileStatuses.success;
        track(XML_UPLOAD_FILE, {
            path: modalSettings?.pathToTable,
            uploadFile: file.uploadFile.name,
            size: file.uploadFile.size,
            status: status,
        });
        return {
            id: file.id,
            uploadFile: file.uploadFile,
            status: status,
            errorMessage: <FileException>{errorMessageText}</FileException>,
            driveUri: file.driveUri,
            instancesInfo: {
                count: instancesCount != undefined ? Number(instancesCount) : undefined,
                description: modalSettings?.instanceCountFromProps?.description,
            },
        };
    }

    const uploadFiles = async (combineMethod: FufCombineMethod) => {
        setLoading(true);

        const response = await importXmls(validDriveURLs, combineMethod, CombinatorType.Default, false, fufCombineArgs);
        if (response !== undefined) {
            if (response.addedItemsCount !== 0 || response.updatedItemsCount !== 0) {
                setMergeResult(response);
                setStep(PatchFufSteps.ShowCombineResult);
                await form.reloadInner();
                form.filteringManager.resetAllMultilinePages();
            } else {
                await onClose();
                Toast.push("В файле нет данных для загрузки");
            }
        } else {
            setStep(PatchFufSteps.SelectFiles);
            Toast.push("Не удалось загрузить файл", {
                label: "Повторить",
                handler: refreshUpload,
            });
        }
        setLoading(false);
    };

    const getDriveUrls = () =>
        uploadedFiles
            .map(file => (file.status !== UploadFileStatuses.error ? file.driveUri : undefined))
            .filter(isNotNullOrEmpty);

    const setCombineMethodAndUploadFiles = async (combineMethod: FufCombineMethod, args?: string[]) => {
        setStep(PatchFufSteps.SelectFiles);
        setFufCombineMethod(combineMethod);
        setFufCombineArgs(args ?? []);
        await addUserFilesToDrive();
    };

    const clickOnReplaceButton = async () => {
        track(XML_UPLOAD_SELECT_REPLACE, { path: modalSettings?.pathToTable });
        await setCombineMethodAndUploadFiles(FufCombineMethod.ReplaceThenMerge);
    };

    const clickOnAddButton = async () => {
        track(XML_UPLOAD_SELECT_ADD, { path: modalSettings?.pathToTable });
        await setCombineMethodAndUploadFiles(FufCombineMethod.Merge, []);
    };

    const clickOnDeepMergeButton = async () => {
        track(XML_UPLOAD_SELECT_DEEP_MERGE, { path: modalSettings?.pathToTable });
        await setCombineMethodAndUploadFiles(FufCombineMethod.Merge, ["deepMerge"]);
    };

    const onApplyUploadFiles = async () => {
        await setCombineMethodAndUploadFiles(FufCombineMethod.ReplaceThenMerge);
        setStep(PatchFufSteps.SelectFiles);
    };

    const isSomeFileLoading = uploadedFiles.some(
        file => file.status === UploadFileStatuses.new || file.status === UploadFileStatuses.progress
    );

    const isButtonLoadDisabled = loading || !Boolean(getDriveUrls().length) || isSomeFileLoading;

    const isHaveValidAndInvalidFile =
        uploadedFiles.some(item => item.status === UploadFileStatuses.error) &&
        uploadedFiles.some(item => item.status === UploadFileStatuses.success);

    const isStaticAndSelectFufMethod =
        step === PatchFufSteps.SelectCombineFufMethod && modalSettings?.tableType === TableType.static;

    const isDynamicAndSelectFufMethod =
        step === PatchFufSteps.SelectCombineFufMethod && modalSettings?.tableType === TableType.dynamic;

    return (
        <Modal width={568} ignoreBackgroundClick onClose={onClose} noClose={step === PatchFufSteps.LoadToForm}>
            <Modal.Header>{modalHeader}</Modal.Header>
            <Modal.Body style={{ paddingBottom: 16 }}>
                <ModalLoader loading={loading}>
                    <>
                        <StepPage visible={isStaticAndSelectFufMethod}>
                            <ModalText>Новые данные заменят данные в таблице</ModalText>
                        </StepPage>
                        <StepPage visible={isDynamicAndSelectFufMethod}>
                            <CombineMethodPicker
                                onMergeClick={
                                    modalSettings?.combineType === FufCombineMethod.Merge ||
                                    modalSettings?.combineType === undefined
                                        ? clickOnAddButton
                                        : undefined
                                }
                                onAddToEndClick={
                                    modalSettings?.combineType === FufCombineMethod.AddToEnd
                                        ? clickOnAddButton
                                        : undefined
                                }
                                onReplaceClick={clickOnReplaceButton}
                                onDeepMergeClick={modalSettings?.applyDeepMerge ? clickOnDeepMergeButton : undefined}
                            />
                        </StepPage>
                        <StepPage visible={step === PatchFufSteps.SelectFiles}>{filesContainer}</StepPage>
                        <StepPage visible={step === PatchFufSteps.ShowCombineResult}>
                            <MergeResult mergeResult={mergeResult} />
                        </StepPage>
                    </>
                </ModalLoader>
            </Modal.Body>
            <StepPage visible={isStaticAndSelectFufMethod || step !== PatchFufSteps.SelectCombineFufMethod}>
                <Modal.Footer panel sticky>
                    <StepPage visible={isStaticAndSelectFufMethod}>
                        <Gapped gap={16}>
                            <Button onClick={onApplyUploadFiles} use="primary" size="medium">
                                Загрузить
                            </Button>
                            <Button onClick={onClose} size="medium">
                                Отменить
                            </Button>
                        </Gapped>
                    </StepPage>
                    <StepPage visible={step !== PatchFufSteps.SelectCombineFufMethod}>
                        <Gapped gap={16}>
                            {step !== PatchFufSteps.ShowCombineResult && (
                                <Button
                                    size={"medium"}
                                    data-tid={"UploadButton"}
                                    disabled={isButtonLoadDisabled}
                                    onClick={onUploadFileHandler}
                                    use="primary">
                                    Загрузить файлы
                                    {isHaveValidAndInvalidFile && !isButtonLoadDisabled && " без ошибок"}
                                </Button>
                            )}
                            {!loading && (
                                <Button onClick={onClose} size={"medium"} data-tid={"CloseModalButton"}>
                                    Закрыть
                                </Button>
                            )}
                        </Gapped>
                    </StepPage>
                </Modal.Footer>
            </StepPage>
        </Modal>
    );
};
